import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useRef, useState } from "react";

function tagKey(tag: string): string {
  return tag.trim().toLowerCase();
}

export function TagInput({
  label,
  value,
  onChange,
  suggestions,
  disabled = false,
  placeholder,
  hint,
}: {
  label?: string;
  value: string[];
  onChange: (tags: string[]) => void;
  suggestions: string[];
  disabled?: boolean;
  placeholder?: string;
  hint?: string;
}) {
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedKeys = useMemo(
    () => new Set(value.map((t) => tagKey(t))),
    [value],
  );

  const filteredSuggestions = useMemo(() => {
    const q = draft.trim().toLowerCase();
    return suggestions.filter((s) => {
      if (selectedKeys.has(tagKey(s))) {
        return false;
      }
      if (q === "") {
        return true;
      }
      return s.toLowerCase().includes(q);
    });
  }, [suggestions, draft, selectedKeys]);

  useEffect(() => {
    setActiveIndex(-1);
  }, [draft, filteredSuggestions.length]);

  useEffect(() => {
    function handlePointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  function addTag(raw: string) {
    const t = raw.trim();
    if (!t) {
      return;
    }
    if (value.some((v) => tagKey(v) === tagKey(t))) {
      setDraft("");
      return;
    }
    onChange([...value, t]);
    setDraft("");
    setOpen(false);
    inputRef.current?.focus();
  }

  function removeTag(index: number) {
    onChange(value.filter((_, i) => i !== index));
  }

  function commitDraftOrSelection() {
    if (
      activeIndex >= 0 &&
      activeIndex < filteredSuggestions.length &&
      filteredSuggestions[activeIndex]
    ) {
      addTag(filteredSuggestions[activeIndex]);
      return;
    }
    addTag(draft);
  }

  return (
    <div ref={rootRef} className="w-full">
      {label && (
        <label
          className={`block text-sm font-medium ${disabled ? "text-gray-400" : "text-gray-500"}`}
        >
          {label}
        </label>
      )}
      <div
        className={`mt-1 w-full border-1 border-gray-300 rounded-xs p-2 flex flex-wrap gap-1.5 items-center min-h-[42px] ${disabled ? "bg-gray-100 cursor-not-allowed" : "bg-white"}`}
      >
        {value.map((tag, index) => (
          <span
            key={`${tag}-${index}`}
            className="inline-flex items-center gap-1 rounded-full bg-gray-200 pl-2.5 pr-1 py-0.5 text-sm text-gray-800 max-w-full"
          >
            <span className="truncate">{tag}</span>
            <button
              aria-label={`Remove ${tag}`}
              className="shrink-0 p-0.5 rounded-full hover:bg-gray-300 text-gray-600 disabled:opacity-40"
              disabled={disabled}
              type="button"
              onClick={() => removeTag(index)}
            >
              <FontAwesomeIcon className="text-xs" icon={faTimes} />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          className="flex-1 min-w-[8rem] border-0 outline-none bg-transparent text-base disabled:cursor-not-allowed disabled:text-gray-400"
          disabled={disabled}
          placeholder={value.length === 0 ? placeholder : undefined}
          type="text"
          value={draft}
          onBlur={() => {
            setOpen(false);
          }}
          onChange={(e) => {
            setDraft(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (disabled) {
              return;
            }
            if (e.key === "Enter") {
              e.preventDefault();
              commitDraftOrSelection();
              return;
            }
            if (e.key === ",") {
              e.preventDefault();
              if (draft.trim() !== "") {
                addTag(draft);
              }
              return;
            }
            if (e.key === "Backspace" && draft === "" && value.length > 0) {
              removeTag(value.length - 1);
              return;
            }
            if (e.key === "Escape") {
              setOpen(false);
              setActiveIndex(-1);
              return;
            }
            if (e.key === "ArrowDown") {
              e.preventDefault();
              if (filteredSuggestions.length === 0) {
                return;
              }
              setOpen(true);
              setActiveIndex((i) =>
                i < filteredSuggestions.length - 1 ? i + 1 : 0,
              );
              return;
            }
            if (e.key === "ArrowUp") {
              e.preventDefault();
              if (filteredSuggestions.length === 0) {
                return;
              }
              setOpen(true);
              setActiveIndex((i) =>
                i > 0 ? i - 1 : filteredSuggestions.length - 1,
              );
            }
          }}
        />
      </div>
      {open && !disabled && filteredSuggestions.length > 0 && (
        <ul
          className="relative z-20 mt-1 max-h-40 overflow-y-auto border-1 border-gray-300 rounded-xs bg-white shadow-sm"
          role="listbox"
        >
          {filteredSuggestions.map((s, i) => (
            <li key={s}>
              <button
                aria-selected={i === activeIndex}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${i === activeIndex ? "bg-gray-100" : ""}`}
                role="option"
                type="button"
                onClick={() => addTag(s)}
                onMouseDown={(ev) => ev.preventDefault()}
                onMouseEnter={() => setActiveIndex(i)}
              >
                {s}
              </button>
            </li>
          ))}
        </ul>
      )}
      {hint && <p className="text-sm text-gray-500 mt-1">{hint}</p>}
    </div>
  );
}
